/**
 * Invoices and payment plans.
 *
 * Pure functions, like pricing.ts: integer cents inside, rand at the edges, no
 * clock, no database.
 *
 * A booking gets ONE invoice with a payment plan (15 Oct 2026, how Jena works
 * in Studio Ninja): the quote's lines, and a schedule of instalments such as
 * "40 % within 7 days, the rest 14 days before the wedding". Amounts are exact:
 * each share is rounded once and the last instalment takes the remainder, so
 * the schedule always adds up to the invoice total to the cent.
 *
 * Payments are not tied to an instalment. They are applied in order, oldest
 * instalment first (`applyPayments`), which is how a couple thinks about it:
 * "I paid the deposit, now I owe the balance".
 */

import { formatDateLong, shiftDays, type IsoDate } from './dates'

// ---------------------------------------------------------------------------
// Cents
// ---------------------------------------------------------------------------

const toCents = (rand: number) => (rand < 0 ? -Math.round(-rand * 100) : Math.round(rand * 100))
const toRand = (cents: number) => cents / 100

// ---------------------------------------------------------------------------
// Plans → instalments
// ---------------------------------------------------------------------------

export type DueRule = 'on_issue' | 'days_after_issue' | 'days_before_event' | 'on_event' | 'days_after_event'

export interface PlanStep {
  label: string
  /** Fraction of the total. Null on the step that takes what is left. */
  share: number | null
  due_rule: DueRule
  offset_days: number
}

export interface PlannedInstalment {
  label: string
  /** Rand. */
  amount: number
  dueDate: IsoDate
}

export interface InstalmentPlan {
  instalments: PlannedInstalment[]
  /** Things to look at before sending, a sentence each. */
  warnings: string[]
}

export function dueDateFor(step: Pick<PlanStep, 'due_rule' | 'offset_days'>, issueDate: IsoDate, eventDate: IsoDate): IsoDate {
  switch (step.due_rule) {
    case 'on_issue':
      return issueDate
    case 'days_after_issue':
      return shiftDays(issueDate, step.offset_days)
    case 'days_before_event':
      return shiftDays(eventDate, -step.offset_days)
    case 'on_event':
      return eventDate
    case 'days_after_event':
      return shiftDays(eventDate, step.offset_days)
  }
}

/**
 * Turns a plan into dated instalments for a total.
 *
 * Steps with a share take that share, rounded once; the step without one
 * takes the remainder (if every step has a share, the last one absorbs the
 * rounding). No instalment is due before the invoice is issued, and dates
 * never run backwards: a late booking whose balance date has already passed
 * gets that instalment (and any before it) due on the day of issue, with a
 * warning.
 */
export function planInstalments(
  steps: PlanStep[],
  total: number,
  issueDate: IsoDate,
  eventDate: IsoDate,
): InstalmentPlan {
  if (steps.length === 0) throw new Error('A payment plan needs at least one step.')

  const totalCents = toCents(total)
  const shares = steps.map((step) => step.share ?? 0).reduce((sum, share) => sum + share, 0)
  if (shares > 1 + 1e-9) throw new Error('The shares in this plan add up to more than 100 %.')

  const remainderIndex = (() => {
    const open = steps.findIndex((step) => step.share === null)
    return open === -1 ? steps.length - 1 : open
  })()

  const amounts = steps.map((step, index) => (index === remainderIndex ? 0 : Math.round(totalCents * (step.share ?? 0))))
  amounts[remainderIndex] = totalCents - amounts.reduce((sum, cents) => sum + cents, 0)

  const warnings: string[] = []

  const dues = steps.map((step) => {
    const due = dueDateFor(step, issueDate, eventDate)
    if (due >= issueDate) return due
    warnings.push(`${step.label} would have been due on ${formatDateLong(due)}, which has passed, so it is due on ${formatDateLong(issueDate)} instead.`)
    return issueDate
  })

  // Dates never run backwards: an earlier instalment is never due after a
  // later one (a deposit "in 7 days" for a wedding next week is due with the
  // balance).
  for (let index = dues.length - 2; index >= 0; index--) {
    const next = dues[index + 1] as IsoDate
    if ((dues[index] as IsoDate) > next) dues[index] = next
  }

  const instalments = steps.map((step, index): PlannedInstalment => ({
    label: step.label,
    amount: toRand(amounts[index] ?? 0),
    dueDate: dues[index] as IsoDate,
  }))

  // A zero instalment (a 100 % deposit, say) is not a payment anyone makes.
  return { instalments: instalments.filter((item) => item.amount > 0), warnings }
}

/**
 * Equal monthly instalments for a custom plan: the first on `firstDue`, then
 * one a month, the last taking the rounding. Her one couple on a monthly plan
 * paid R3 290 a month.
 */
export function monthlyInstalments(total: number, count: number, firstDue: IsoDate, label = 'Instalment'): PlannedInstalment[] {
  if (!Number.isInteger(count) || count < 1) throw new Error('A monthly plan needs at least one payment.')
  const totalCents = toCents(total)
  const each = Math.floor(totalCents / count)
  const [year = 0, month = 1, day = 1] = firstDue.split('-').map(Number)

  return Array.from({ length: count }, (_, index) => {
    // Same day of the month, clamped to the month's length (31 Jan → 28 Feb).
    const target = new Date(Date.UTC(year, month - 1 + index, 1))
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
    target.setUTCDate(Math.min(day, lastDay))
    const cents = index === count - 1 ? totalCents - each * (count - 1) : each
    return { label: `${label} ${index + 1} of ${count}`, amount: toRand(cents), dueDate: target.toISOString().slice(0, 10) }
  })
}

// ---------------------------------------------------------------------------
// Payments → where each instalment stands
// ---------------------------------------------------------------------------

export interface InstalmentLike {
  label: string
  amount: number | string
  due_date: string
}

export interface InstalmentStanding {
  label: string
  amount: number
  dueDate: IsoDate
  paid: number
  outstanding: number
  status: 'paid' | 'part_paid' | 'due' | 'overdue'
}

/** Applies the money received to the instalments in date order. */
export function applyPayments(instalments: InstalmentLike[], amountPaid: number | string, onDate: IsoDate): InstalmentStanding[] {
  let left = toCents(Number(amountPaid))

  return [...instalments]
    .sort((a, b) => a.due_date.localeCompare(b.due_date))
    .map((instalment) => {
      const amount = toCents(Number(instalment.amount))
      const paid = Math.min(amount, Math.max(0, left))
      left -= paid
      const outstandingCents = amount - paid
      const status: InstalmentStanding['status'] =
        outstandingCents === 0 ? 'paid' : instalment.due_date < onDate ? 'overdue' : paid > 0 ? 'part_paid' : 'due'
      return {
        label: instalment.label,
        amount: toRand(amount),
        dueDate: instalment.due_date,
        paid: toRand(paid),
        outstanding: toRand(outstandingCents),
        status,
      }
    })
}

/** The next instalment still owed, or null when nothing is. */
export function nextDue(standings: InstalmentStanding[]): InstalmentStanding | null {
  return standings.find((item) => item.outstanding > 0) ?? null
}

// ---------------------------------------------------------------------------
// Invoice-level helpers
// ---------------------------------------------------------------------------

/** Open, overdue, settled: what an invoice needs from her today. */
export type InvoiceStanding = 'draft' | 'open' | 'overdue' | 'paid' | 'void'

export function invoiceStanding(
  invoice: { status: string; due_date: string; amount_paid?: number | string; instalments?: InstalmentLike[] },
  onDate: IsoDate,
): InvoiceStanding {
  switch (invoice.status) {
    case 'draft':
      return 'draft'
    case 'paid':
      return 'paid'
    case 'void':
      return 'void'
  }
  if (invoice.instalments && invoice.instalments.length > 0) {
    const late = applyPayments(invoice.instalments, invoice.amount_paid ?? 0, onDate).some((item) => item.status === 'overdue')
    return late ? 'overdue' : 'open'
  }
  return invoice.due_date < onDate ? 'overdue' : 'open'
}

export function outstanding(invoice: { total: number | string; amount_paid: number | string }): number {
  return toRand(toCents(Number(invoice.total)) - toCents(Number(invoice.amount_paid)))
}

/** "DLC0025": the number as printed, in her Studio Ninja format. */
export function formatInvoiceNumber(prefix: string, n: number): string {
  return `${prefix}${String(n).padStart(4, '0')}`
}

// ---------------------------------------------------------------------------
// The booking invoice, from an accepted quote
// ---------------------------------------------------------------------------

export interface QuoteLineLike {
  source: string
  description: string
  detail: string | null
  quantity: number | string
  unit_price: number | string
  line_total: number | string
}

export interface PlannedLine {
  description: string
  detail: string | null
  quantity: number
  unitPrice: number
  lineTotal: number
}

/**
 * The quote's lines, as invoice lines. VAT is a total on the invoice, not a
 * line, so the quote's VAT line is left out; the working ("detail") is kept
 * only for travel, where a couple wants to see the kilometres.
 */
export function invoiceLinesFromQuote(lines: QuoteLineLike[]): PlannedLine[] {
  return lines
    .filter((line) => line.source !== 'vat')
    .map((line) => ({
      description: line.description,
      detail: line.source === 'travel' ? line.detail : null,
      quantity: Number(line.quantity),
      unitPrice: Number(line.unit_price),
      lineTotal: Number(line.line_total),
    }))
}
